import React, { useState } from "react";
import PriceCard from "./PriceCard";
import { Chip } from "@mui/material";

interface StockVsIndexCardProps {
  data: any;
  tickerFilter: string | null;
  isSearchMatch: (...fields: (string | undefined)[]) => boolean;
  handleInfoBox: (boxType: number) => void;
  showInfoBox: boolean;
  highlightBox: number;
  highlightBoxEnum: any;
  setInfoBoxRef: (index: number) => (ref: HTMLDivElement | null) => void;
}

const StockVsIndexCard: React.FC<StockVsIndexCardProps> = ({
  data,
  tickerFilter,
  isSearchMatch,
  handleInfoBox,
  showInfoBox,
  highlightBox,
  highlightBoxEnum,
  setInfoBoxRef,
}) => {
  const [stockVsIndexTickerSortOrder, setStockVsIndexTickerSortOrder] =
    useState("asc");
  const [stockVsIndexStockPctSortOrder, setStockVsIndexStockPctSortOrder] =
    useState("asc");
  const [stockVsIndexIndexPctSortOrder, setStockVsIndexIndexPctSortOrder] =
    useState("asc");
  const [activeStockVsIndexSortType, setActiveStockVsIndexSortType] =
    useState("ticker");

  const sortedStockVsIndexData = [...Object.keys(data?.stock_vs_index || {})]
    .flatMap((key) => data.stock_vs_index?.[key] || [])
    .sort((a, b) => {
      if (activeStockVsIndexSortType === "ticker") {
        return stockVsIndexTickerSortOrder === "asc"
          ? a.stock_symbol.localeCompare(b.stock_symbol)
          : b.stock_symbol.localeCompare(a.stock_symbol);
      } else if (activeStockVsIndexSortType === "stockPctChange") {
        return stockVsIndexStockPctSortOrder === "asc"
          ? a.Stock_pct_change - b.Stock_pct_change
          : b.Stock_pct_change - a.Stock_pct_change;
      } else if (activeStockVsIndexSortType === "indexPctChange") {
        return stockVsIndexIndexPctSortOrder === "asc"
          ? a.Index_pct_change - b.Index_pct_change
          : b.Index_pct_change - a.Index_pct_change;
      }
      return 0;
    });

  const toggleStockVsIndexTickerSort = () => {
    setStockVsIndexTickerSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveStockVsIndexSortType("ticker");
  };

  const toggleStockVsIndexStockPctSort = () => {
    setStockVsIndexStockPctSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveStockVsIndexSortType("stockPctChange");
  };

  const toggleStockVsIndexIndexPctSort = () => {
    setStockVsIndexIndexPctSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    setActiveStockVsIndexSortType("indexPctChange");
  };

  if (
    tickerFilter &&
    sortedStockVsIndexData.filter((key) => isSearchMatch(key.stock_symbol))
      .length === 0
  ) {
    return null;
  }

  const headerStyle: React.CSSProperties = {
    cursor: "pointer",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    userSelect: "none",
  };

  return (
    <PriceCard
      tooltipText="Checks whether a sector's index increases or decreases while the company's stock belonging to the specific sector, increases or decreases."
      toggleInfoBox={() => handleInfoBox(highlightBoxEnum.StockVsIndex)}
      highlightBox={showInfoBox && highlightBox === highlightBoxEnum.StockVsIndex}
      setInfoBoxRef={setInfoBoxRef(highlightBoxEnum.StockVsIndex)}
      title={
        <div className="svi-title-bar">
          <div className="svi-title-left">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1a2f4e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
            </svg>
            <span className="svi-title-text">Stock vs Index</span>
          </div>
        </div>
      }
      headers={[
        <div style={headerStyle} onClick={toggleStockVsIndexTickerSort} key="h1">
          Ticker {stockVsIndexTickerSortOrder === "asc" ? "↑" : "↓"}
        </div>,
        <div style={{ ...headerStyle, cursor: "default" }} key="h2">
          Index
        </div>,
        <div style={headerStyle} onClick={toggleStockVsIndexStockPctSort} key="h3">
          Stock % {stockVsIndexStockPctSortOrder === "asc" ? "↑" : "↓"}
        </div>,
        <div style={headerStyle} onClick={toggleStockVsIndexIndexPctSort} key="h4">
          Index % {stockVsIndexIndexPctSortOrder === "asc" ? "↑" : "↓"}
        </div>,
      ]}
      values={sortedStockVsIndexData
        .filter((key) => isSearchMatch(key.stock_symbol))
        .map((value, i) => [
          <div
            className="ticker-name-trending"
            onClick={() => {
              const ticker = value.stock_symbol;
              if (ticker) {
                window.localStorage.setItem("selectedTab", "0");
                window.localStorage.setItem("tickerId", ticker);
                window.location.href = `/detail`;
              }
            }}
            key={`ticker-${i}`}
          >
            {value.stock_symbol}
          </div>,
          <div key={`index-${i}`} className="svi-index-name">
            {value.Index}
          </div>,
          <div key={`stock-change-${i}`}>
            <Chip
              size="small"
              label={`${value.Stock_pct_change > 0 ? "+" : ""}${value.Stock_pct_change.toFixed(2)}%`}
              sx={{
                borderRadius: "6px",
                color: value.Stock_pct_change > 0 ? "#059669" : "#dc2626",
                backgroundColor: value.Stock_pct_change > 0 ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)",
                fontWeight: 700,
                fontSize: "11px",
                height: "26px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
          </div>,
          <div key={`index-change-${i}`}>
            <Chip
              size="small"
              label={`${value.Index_pct_change > 0 ? "+" : ""}${value.Index_pct_change.toFixed(2)}%`}
              sx={{
                borderRadius: "6px",
                color: value.Index_pct_change > 0 ? "#059669" : "#dc2626",
                backgroundColor: value.Index_pct_change > 0 ? "rgba(5,150,105,0.08)" : "rgba(220,38,38,0.08)",
                fontWeight: 700,
                fontSize: "11px",
                height: "26px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            />
          </div>,
        ])
        .flat()}
    />
  );
};

export default StockVsIndexCard;
